import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import { timeAgo } from 'lib/timeAgo';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  DO_MANUAL_SYNC,
  DO_NETWORK_CHECK_UNTIL_ONLINE,
  NAVIGATE_MODAL,
  PAUSE_BG_NETWORK,
  doAuthShowAuthModal,
  UNPAUSE_BG_NETWORK,
} from 'store/actions';
import {
  selectLastOfflineTime,
  selectLastOnlineTime,
} from 'store/app/selectors';
import {
  selectHasAssetErrors,
  selectIsNotAuthorized,
} from 'store/error/selectors';
import {
  selectLastDataSync,
  selectSyncErrors,
  selectSyncFileDownloadQueueCount,
  selectSyncStatus,
  selectSyncFileUploadQueueCount,
  selectSyncThumbnailDownloadQueueCount,
} from 'store/sync/selectors';
import { SyncSteps } from 'store/sync/types';

type Props = {
  onTapOption: () => void;
  onSyncFinished: () => void;
};

const enum ConnectionState {
  'running' = 'running',
  'done' = 'done',
  'errorOffline' = 'errorOffline',
  'errorAuth' = 'errorAuth',
}

const enum StepState {
  'waiting' = 'waiting',
  'running' = 'running',
  'error' = 'error',
  'done' = 'done',
}

export const SyncSummaryCards: React.FC<Props> = ({
  onTapOption,
  onSyncFinished,
}) => {
  const dispatch = useDispatch();

  const syncStatus = useSelector(selectSyncStatus);
  const syncErrors = useSelector(selectSyncErrors);
  const lastOnlineTime = useSelector(selectLastOnlineTime);
  const lastOfflineTime = useSelector(selectLastOfflineTime);
  const isNotAuthorized = useSelector(selectIsNotAuthorized);
  const hasAssetErrors = useSelector(selectHasAssetErrors);
  const lastDataSync = useSelector(selectLastDataSync);

  const thumbDownloadQueueCount = useSelector(
    selectSyncThumbnailDownloadQueueCount,
  );
  const initThumbDownloadQueueCountRef = React.useRef(thumbDownloadQueueCount);
  const showThumbDownloadCard = !!initThumbDownloadQueueCountRef.current;

  const fileDownloadQueueCount = useSelector(selectSyncFileDownloadQueueCount);
  const initFileDownloadQueueCountRef = React.useRef(fileDownloadQueueCount);
  const showFileDownloadCard = !!initFileDownloadQueueCountRef.current;

  const fileUploadQueueCount = useSelector(selectSyncFileUploadQueueCount);
  const initFileUploadQueueCountRef = React.useRef(fileUploadQueueCount);
  const showFileUploadCard = !!initFileUploadQueueCountRef.current;

  const startTimeRef = React.useRef<number>(Date.now());

  const stepConnection = React.useRef<{
    state: ConnectionState;
    error: string;
  }>({
    state: ConnectionState.running,
    error: '',
  });
  const stepDataSync = React.useRef<{ state: StepState; error: string }>({
    state: StepState.waiting,
    error: '',
  });
  const stepDownThumbs = React.useRef<{
    state: StepState;
    error: string;
  }>({
    state: StepState.waiting,
    error: '',
  });
  const stepDownFiles = React.useRef<{
    state: StepState;
    error: string;
  }>({
    state: StepState.waiting,
    error: '',
  });
  const stepUpFiles = React.useRef<{
    state: StepState;
    error: string;
  }>({
    state: StepState.waiting,
    error: '',
  });
  const stepWasStarted = React.useRef<Record<SyncSteps, boolean>>({
    [SyncSteps.data]: false,
    [SyncSteps.fileDownload]: false,
    [SyncSteps.fileUpload]: false,
    [SyncSteps.thumbnailDownload]: false,
    [SyncSteps.waiting]: false,
    [SyncSteps.finished]: false,
  });

  stepWasStarted.current[syncStatus.step] = true;
  let continueToNext: boolean = false;

  // Connect
  if (lastOnlineTime && startTimeRef.current < lastOnlineTime) {
    stepConnection.current.state = isNotAuthorized
      ? ConnectionState.errorAuth
      : ConnectionState.done;
    continueToNext = true;
  } else if (lastOfflineTime && startTimeRef.current < lastOfflineTime) {
    stepConnection.current.state = ConnectionState.errorOffline;
  }

  if (syncStatus.step === SyncSteps.waiting) {
    continueToNext = false;
  }

  // Data Sync
  if (
    continueToNext &&
    ![StepState.done, StepState.error].includes(stepDataSync.current.state)
  ) {
    continueToNext = false;
    if (syncStatus.step === SyncSteps.data) {
      stepDataSync.current.state = StepState.running;
    } else {
      stepDataSync.current.state =
        syncErrors[SyncSteps.data] || hasAssetErrors
          ? StepState.error
          : StepState.done;
      stepDataSync.current.error = syncErrors[SyncSteps.data];
      continueToNext = true;
    }
  }

  // Download Thumbnails
  if (
    continueToNext &&
    ![StepState.done, StepState.error].includes(stepDownThumbs.current.state)
  ) {
    continueToNext = false;
    if (syncStatus.step === SyncSteps.thumbnailDownload) {
      stepDownThumbs.current.state = StepState.running;
    } else {
      stepDownThumbs.current.state =
        stepDownThumbs.current.state === StepState.running &&
        syncErrors[SyncSteps.thumbnailDownload]
          ? StepState.error
          : StepState.done;
      stepDownThumbs.current.error = syncErrors[SyncSteps.thumbnailDownload];
      continueToNext = true;
    }
  }

  // Download Files
  if (
    continueToNext &&
    ![StepState.done, StepState.error].includes(stepDownFiles.current.state)
  ) {
    continueToNext = false;
    if (syncStatus.step === SyncSteps.fileDownload) {
      stepDownFiles.current.state = StepState.running;
    } else {
      stepDownFiles.current.state =
        stepDownFiles.current.state === StepState.running &&
        syncErrors[SyncSteps.fileDownload]
          ? StepState.error
          : StepState.done;
      stepDownFiles.current.error = syncErrors[SyncSteps.fileDownload];
      continueToNext = true;
    }
  }

  // File Upload
  if (
    continueToNext &&
    ![StepState.done, StepState.error].includes(stepUpFiles.current.state)
  ) {
    continueToNext = false;
    if (syncStatus.step === SyncSteps.fileUpload) {
      stepUpFiles.current.state = StepState.running;
    } else {
      stepUpFiles.current.state =
        stepUpFiles.current.state === StepState.running &&
        syncErrors[SyncSteps.fileUpload]
          ? StepState.error
          : StepState.done;
      stepUpFiles.current.error = syncErrors[SyncSteps.fileUpload];
      continueToNext = true;
    }
  }

  const syncFinished = !!stepWasStarted.current[SyncSteps.finished];
  const stepConnectionState = stepConnection.current.state;
  const stepDataSyncState = stepDataSync.current.state;
  const stepDownThumbState = stepDownThumbs.current.state;
  const stepDownFileState = stepDownFiles.current.state;
  const stepUpFileState = stepUpFiles.current.state;

  React.useEffect(() => {
    let networkPaused = false;
    let checkOnlineTimeout: ReturnType<typeof setTimeout> | undefined;
    if (
      stepConnectionState === ConnectionState.running ||
      stepConnectionState === ConnectionState.errorOffline
    ) {
      networkPaused = true;
      dispatch(PAUSE_BG_NETWORK());
      checkOnlineTimeout = setTimeout(
        () => dispatch(DO_NETWORK_CHECK_UNTIL_ONLINE(true)),
        500,
      );
    }
    if (
      stepConnectionState === ConnectionState.done &&
      stepDataSyncState === StepState.waiting
    ) {
      dispatch(DO_MANUAL_SYNC({}));
    }
    return () => {
      if (networkPaused) {
        clearTimeout(checkOnlineTimeout);
        dispatch(DO_NETWORK_CHECK_UNTIL_ONLINE(false));
        dispatch(UNPAUSE_BG_NETWORK());
      }
    };
  }, [dispatch, stepConnectionState, stepDataSyncState]);

  React.useEffect(() => {
    if (syncFinished) {
      dispatch(PAUSE_BG_NETWORK());
      onSyncFinished();
    }
    return () => {
      if (syncFinished) {
        dispatch(UNPAUSE_BG_NETWORK());
      }
    };
  }, [dispatch, onSyncFinished, syncFinished]);

  const CardConnection = React.useMemo(() => {
    let label = 'Connection';
    let value = lastOnlineTime
      ? `Last connected ${timeAgo.format(new Date(lastOnlineTime))}`
      : 'Last connection not known';
    let icon: ICONS = ICONS.RotateDuotone;
    let iconColor: undefined | string;
    let showSignInButton: boolean = false;

    switch (stepConnectionState) {
      case ConnectionState.done:
        label = 'Connected';
        value = 'Successfully connected to RiskBase';
        icon = ICONS.CircleCheckLight;
        iconColor = theme.colors.green;
        break;
      case ConnectionState.errorOffline:
        label = 'Offline, trying to connect...';
        icon = ICONS.RotateDuotone;
        iconColor = theme.colors.red;
        break;
      case ConnectionState.errorAuth:
        label = 'Sign-in Required';
        value = 'Sign-in to continue using RiskBase';
        icon = ICONS.KeyLight;
        iconColor = theme.colors.red;
        showSignInButton = true;
        break;
    }
    return (
      <CardRow
        label={label}
        label2={value}
        icon={icon}
        labelColor={iconColor}
        label2Color={iconColor}
        iconColor={iconColor}
        iconRotate={icon === ICONS.RotateDuotone}
        style={styles.itemCard}
      >
        {showSignInButton && (
          <View style={{ marginLeft: theme.padding }}>
            <Button
              text="Sign In"
              onPress={() => {
                onTapOption();
                dispatch(doAuthShowAuthModal({ actionAfterAuth: undefined }));
              }}
            />
          </View>
        )}
      </CardRow>
    );
  }, [stepConnectionState, dispatch, lastOnlineTime, onTapOption]);

  const CardDataSync = React.useMemo(() => {
    const text = 'Data Sync';
    let value = lastDataSync
      ? `Last synced ${timeAgo.format(new Date(lastDataSync))}`
      : 'Last sync unknown';
    let icon: ICONS = ICONS.ClockLight;
    let iconColor: undefined | string;
    let showIssueButton: boolean = false;

    switch (stepDataSyncState) {
      case StepState.running:
        icon = ICONS.RotateDuotone;
        break;
      case StepState.error:
        if (stepDataSync.current.error) {
          value = stepDataSync.current.error;
        } else {
          value = 'There are sync issues';
          showIssueButton = true;
        }
        icon = ICONS.CircleExclamationLight;
        iconColor = theme.colors.red;
        break;
      case StepState.done:
        iconColor = theme.colors.green;
        icon = ICONS.CircleCheckLight;
        break;
    }
    return (
      <CardRow
        label={text}
        label2={value}
        icon={icon}
        labelColor={iconColor}
        label2Color={iconColor}
        iconColor={iconColor}
        iconRotate={icon === ICONS.RotateDuotone}
        disabled={stepDataSyncState === StepState.waiting}
        style={styles.itemCard}
      >
        {showIssueButton && (
          <View style={{ marginLeft: theme.padding }}>
            <Button
              text="Fix Issues"
              onPress={() => {
                onTapOption();
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.SYNCERRORS,
                    params: {},
                  }),
                );
              }}
            />
          </View>
        )}
      </CardRow>
    );
  }, [lastDataSync, stepDataSyncState, onTapOption, dispatch]);

  const CardDownloadThumbnails = React.useMemo(() => {
    if (!showThumbDownloadCard) {
      return null;
    }
    const text = 'Thumbnail Downloads';
    let value = !thumbDownloadQueueCount
      ? pluralise(
          initThumbDownloadQueueCountRef.current,
          '{x} thumbnail downloaded',
          '{x} thumbnails downloaded',
          'No thumbnails to download',
        )
      : pluralise(
          thumbDownloadQueueCount,
          '1 thumbnail to download',
          '{x} thumbnails to download',
          'No thumbnails to download',
        );
    let icon: ICONS = ICONS.ClockLight;
    let iconColor: undefined | string;

    switch (stepDownThumbState) {
      case StepState.running:
        icon = ICONS.RotateDuotone;
        break;
      case StepState.error:
        icon = ICONS.CircleExclamationLight;
        iconColor = theme.colors.red;
        value =
          `${stepDownThumbs.current.error}. ` +
          `${pluralise(
            thumbDownloadQueueCount,
            '1 thumbnail not downloaded',
            '{x} thumbnails not downloaded',
          )}` +
          ', try syncing again';
        break;
      case StepState.done:
        if (thumbDownloadQueueCount) {
          icon = ICONS.CircleExclamationLight;
          iconColor = theme.colors.red;
          value = `${pluralise(
            thumbDownloadQueueCount,
            '1 thumbnail not downloaded',
            '{x} thumbnails not downloaded',
          )}, try syncing again`;
        } else {
          iconColor = theme.colors.green;
          icon = ICONS.CircleCheckLight;
        }
        break;
    }
    return (
      <CardRow
        label={text}
        label2={value}
        icon={icon}
        labelColor={iconColor}
        label2Color={iconColor}
        iconColor={iconColor}
        iconRotate={icon === ICONS.RotateDuotone}
        disabled={stepDownThumbState === StepState.waiting}
        style={styles.itemCard}
      />
    );
  }, [showThumbDownloadCard, stepDownThumbState, thumbDownloadQueueCount]);

  const CardDownloadFiles = React.useMemo(() => {
    if (!showFileDownloadCard) {
      return null;
    }
    const text = 'File Downloads';
    let value = !fileDownloadQueueCount
      ? pluralise(
          initFileDownloadQueueCountRef.current,
          '{x} file downloaded',
          '{x} files downloaded',
          'No files to download',
        )
      : pluralise(
          fileDownloadQueueCount,
          '1 file to download',
          '{x} files to download',
          'No files to download',
        );
    let icon: ICONS = ICONS.ClockLight;
    let iconColor: undefined | string;

    switch (stepDownFileState) {
      case StepState.running:
        icon = ICONS.RotateDuotone;
        break;
      case StepState.error:
        icon = ICONS.CircleExclamationLight;
        iconColor = theme.colors.red;
        value =
          `${stepDownThumbs.current.error}. ` +
          `${pluralise(
            thumbDownloadQueueCount,
            '1 file not downloaded',
            '{x} files not downloaded',
          )}` +
          ', try syncing again';
        break;
      case StepState.done:
        if (fileDownloadQueueCount) {
          icon = ICONS.CircleExclamationLight;
          iconColor = theme.colors.red;
          value = `${pluralise(
            fileDownloadQueueCount,
            '1 file not downloaded',
            '{x} files not downloaded',
          )}, try syncing again`;
        } else {
          iconColor = theme.colors.green;
          icon = ICONS.CircleCheckLight;
        }
        break;
    }
    return (
      <CardRow
        label={text}
        label2={value}
        icon={icon}
        labelColor={iconColor}
        label2Color={iconColor}
        iconColor={iconColor}
        iconRotate={icon === ICONS.RotateDuotone}
        disabled={stepDownFileState === StepState.waiting}
        style={styles.itemCard}
      />
    );
  }, [
    fileDownloadQueueCount,
    showFileDownloadCard,
    stepDownFileState,
    thumbDownloadQueueCount,
  ]);

  const CardUploadFiles = React.useMemo(() => {
    if (!showFileUploadCard) {
      return null;
    }
    const text = 'File Uploads';
    let value = !fileUploadQueueCount
      ? pluralise(
          initFileUploadQueueCountRef.current,
          '{x} file uploaded',
          '{x} files uploaded',
          'No files to uploaded',
        )
      : pluralise(
          fileUploadQueueCount,
          '1 file to upload',
          '{x} files to upload',
          'No files to upload',
        );
    let icon: ICONS = ICONS.ClockLight;
    let iconColor: undefined | string;

    if (stepUpFileState === StepState.running) {
      icon = ICONS.RotateDuotone;
    } else if (stepUpFileState === StepState.done) {
      if (fileUploadQueueCount) {
        icon = ICONS.CircleExclamationLight;
        iconColor = theme.colors.red;
        value = `${pluralise(
          fileUploadQueueCount,
          '1 file',
          '{x} files',
        )} not uploaded, try syncing again`;
      } else {
        iconColor = theme.colors.green;
        icon = ICONS.CircleCheckLight;
      }
    }
    return (
      <CardRow
        label={text}
        label2={value}
        icon={icon}
        labelColor={iconColor}
        label2Color={iconColor}
        iconColor={iconColor}
        iconRotate={icon === ICONS.RotateDuotone}
        disabled={stepUpFileState === StepState.waiting}
        style={styles.itemCard}
      />
    );
  }, [showFileUploadCard, fileUploadQueueCount, stepUpFileState]);

  return (
    <Card>
      {CardConnection}
      {CardDataSync}
      {CardDownloadThumbnails}
      {CardDownloadFiles}
      {CardUploadFiles}
    </Card>
  );
};

const styles = StyleSheet.create({
  itemCard: {
    minHeight: 65,
  },
});
